import React, { useState, useEffect } from 'react';
import {
    Drawer, Box, Typography, Avatar, Table, TableHead, TableBody,
    TableRow, TableCell, TableContainer, TextField, Button, Chip,
    IconButton, Tooltip, ToggleButton, ToggleButtonGroup, CircularProgress,
    Alert, Collapse, Divider, Accordion, AccordionSummary, AccordionDetails,
    Select, MenuItem,
} from '@mui/material';
import TimesheetVersionHistory from '../../timesheets/TimesheetVersionHistory';
import { getData, putData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';

import {
    Close as CloseIcon,
    CheckCircleOutline as ApproveIcon,
    CancelOutlined as RejectIcon,
    CalendarToday as CalendarIcon,
    AttachFile as AttachIcon,
    History as HistoryIcon,
    WarningAmber as WarningIcon,
    CheckCircle as CheckedIcon,
    Cancel as CancelledIcon,
    HourglassEmpty as PendingIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    OpenInNew as OpenInNewIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    WorkOutline as WorkIcon,
    Edit as EditIcon,
    Save as SaveIcon,
} from '@mui/icons-material';

// ───────────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────────

function fmtDate(str) {
    if (!str) return '';
    return new Date(str).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function fileSizeLabel(bytes) {
    if (!bytes) return '';
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
    return `${(bytes / 1024).toFixed(0)}KB`;
}

function getFileTypeConfig(mimeType) {
    if (!mimeType) return { label: 'FILE', color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' };

    if (mimeType.includes('pdf'))
        return { label: 'PDF', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5' };

    if (mimeType.includes('image'))
        return { label: 'IMG', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' };

    if (
        mimeType.includes('sheet') ||
        mimeType.includes('excel') ||
        mimeType.includes('csv')
    )
        return { label: 'XLS', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };

    if (
        mimeType.includes('word') ||
        mimeType.includes('document')
    )
        return { label: 'DOC', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' };

    return { label: 'FILE', color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' };
}

const STATUS_CFG = {
    Draft: {
        color: '#64748b',
        bg: '#f1f5f9',
        border: '#cbd5e1',
        dot: '#94a3b8',
    },
    Submitted: {
        color: '#1976d2',
        bg: '#e3f2fd',
        border: '#90caf9',
        dot: '#1976d2',
    },
    Approved: {
        color: '#059669',
        bg: '#ecfdf5',
        border: '#6ee7b7',
        dot: '#059669',
    },
    Rejected: {
        color: '#dc2626',
        bg: '#fef2f2',
        border: '#fca5a5',
        dot: '#dc2626',
    },
    'Pending Level 2': {
        color: '#c2410c',
        bg: '#fff7ed',
        border: '#fdba74',
        dot: '#f59e0b',
    },
};

// ───────────────────────────────────────────────────────────────────────────────
// KPI Pill
// ───────────────────────────────────────────────────────────────────────────────

function StatPill({ icon: Icon, label, value, accent, subtitle, subtitleColor }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: {sm: 0.9, md: 1.5},
                px: {sm: 1.45, md: 1.7 ,lg: 2},
                py: 1.25,
                borderRadius: 2,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                minWidth: 110,
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: `${accent}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Icon sx={{ fontSize: 16, color: accent }} />
            </Box>

            <Box>
                <Typography
                    sx={{
                        fontSize: {sm: 14, md: 18},
                        fontWeight: 800,
                        color: '#0f172a',
                        lineHeight: 1,
                    }}
                >
                    {value}
                </Typography>

                <Typography
                    sx={{
                        fontSize: {sm: 8, md: 10},
                        color: '#94a3b8',
                        fontWeight: 500,
                        mt: 0.25,
                    }}
                >
                    {label}
                </Typography>

                {subtitle && (
                    <Typography
                        sx={{
                            fontSize: {sm: 6, md: 9},
                            color: subtitleColor || '#94a3b8',
                            fontWeight: 600,
                            mt: 0.15,
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

// ───────────────────────────────────────────────────────────────────────────────
// File Chip
// ───────────────────────────────────────────────────────────────────────────────

function FileChip({ file, size = 'md' }) {
    // Support both plain URL strings and {url, name, type} objects
    const fileObj = typeof file === 'string'
        ? { url: file, name: decodeURIComponent(file.split('/').pop() || 'file'), type: '' }
        : file;
    const cfg = getFileTypeConfig(fileObj.type);
    const small = size === 'sm';

    const handleOpen = (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (fileObj.url) {
            window.open(fileObj.url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <Tooltip title={fileObj.name}>
            <Box
                onClick={fileObj.url ? handleOpen : undefined}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: small ? 0.75 : 1,
                    py: small ? 0.25 : 0.5,
                    bgcolor: cfg.bg,
                    border: `1px solid ${cfg.border}`,
                    borderRadius: 1.5,
                    cursor: fileObj.url ? 'pointer' : 'default',
                    transition: 'all .15s ease',
                    '&:hover': fileObj.url
                        ? {
                              transform: 'translateY(-1px)',
                              filter: 'brightness(.97)',
                          }
                        : {},
                }}
            >
                <Box
                    sx={{
                        px: 0.5,
                        height: small ? 13 : 15,
                        bgcolor: cfg.color,
                        color: '#fff',
                        fontSize: 7,
                        fontWeight: 900,
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    {cfg.label}
                </Box>

                <Typography
                    sx={{
                        fontSize: small ? 10 : 11,
                        color: cfg.color,
                        fontWeight: 600,
                        maxWidth: 130,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {fileObj.name}
                </Typography>

                {!small && (
                    <Typography
                        sx={{
                            fontSize: 10,
                            color: '#94a3b8',
                        }}
                    >
                        {fileSizeLabel(fileObj.size)}
                    </Typography>
                )}

                {fileObj.url && (
                    <OpenInNewIcon
                        sx={{
                            fontSize: 10,
                            color: cfg.color,
                            opacity: 0.7,
                        }}
                    />
                )}
            </Box>
        </Tooltip>
    );
}

// ───────────────────────────────────────────────────────────────────────────────
// Attachments
// ───────────────────────────────────────────────────────────────────────────────

function RowAttachments({ attachments }) {
    const [open, setOpen] = useState(false);

    const files = attachments || [];

    if (files.length === 0) {
        return (
            <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>
                —
            </Typography>
        );
    }

    return (
        <Box>
            <Box
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((p) => !p);
                }}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: 0.3,
                    borderRadius: 1.5,
                    bgcolor: open ? '#eef2ff' : '#f8fafc',
                    border: `1px solid ${open ? '#c7d2fe' : '#e2e8f0'}`,
                    cursor: 'pointer',
                }}
            >
                <AttachIcon sx={{ fontSize: 11, color: '#6366f1' }} />

                <Typography
                    sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#6366f1',
                    }}
                >
                    {files.length}
                </Typography>

                {open ? (
                    <ExpandLessIcon sx={{ fontSize: 11, color: '#6366f1' }} />
                ) : (
                    <ExpandMoreIcon sx={{ fontSize: 11, color: '#6366f1' }} />
                )}
            </Box>

            <Collapse in={open}>
                <Box
                    sx={{
                        mt: 0.75,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.4,
                    }}
                >
                    {files.map((f, i) => (
                        <FileChip key={i} file={f} size="sm" />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

// ───────────────────────────────────────────────────────────────────────────────
// Approval Timeline
// ───────────────────────────────────────────────────────────────────────────────

function ApprovalTimeline({ ts, twoLevelEnabled, localEntries }) {
    // Prefer the parent's live localEntries (sorted, post-edit) over the raw prop
    const entries = localEntries || ts.entries || [];

    // Real evidence that L2 actually ran.
    // COMPANY scope never touches entries — it only sets level2_approver_id /
    // level2_approved_at / rejected_by on the Timesheet row itself — so an
    // assigned company-level approver is just as much "L2 in play" as any
    // per-entry signal.
    const isCompanyScopeL2 = !!ts.level2_approver_id;
    const l2Dispatched   = entries.some(e => e.l2_status != null && e.l2_status !== 'NOT_REQUIRED') || isCompanyScopeL2;
    const l2RanCompany   = !!ts.level2_approved_at;

    // ts.status === 'Rejected' is set by BOTH the external approver's company-level
    // rejection and the manager's internal rejection — rejected_by is the only way
    // to tell which stage actually failed.
    const rejectedByExternal = ts.status === 'Rejected'
        && !!ts.level2_approver_id
        && Number(ts.rejected_by) === Number(ts.level2_approver_id);

    const l2ReviewFailed = (entries.some(e => e.l2_status === 'REJECTED') || rejectedByExternal) && ts.status !== 'Approved';
    // L2 is pending while any dispatched entry is still awaiting review, or —
    // for COMPANY scope — while it's still Submitted and the external approver
    // hasn't acted (approved or rejected) yet.
    const l2ReviewPending = ts.status === 'Pending Level 2'
        || (entries.some(e => e.l2_status != null && e.l2_status !== 'NOT_REQUIRED') && entries.some(e => e.l2_status === 'PENDING'))
        || (isCompanyScopeL2 && ts.status === 'Submitted' && !l2RanCompany);
    // Mirror the sidebar's "ALL APPROVED" logic exactly:
    // done = entries needing L2 exist AND every one of them is genuinely APPROVED.
    // "none are PENDING" is not sufficient — an entry that was never dispatched to
    // L2 at all has l2_status: null, which is neither PENDING nor APPROVED.
    const hasL2Entries = entries.some(e => e.needs_l2_approval);
    const l2ReviewDone =
        (hasL2Entries && entries.filter(e => e.needs_l2_approval).every(e => e.l2_status === 'APPROVED') && !l2ReviewFailed) ||
        l2RanCompany;

    // Show L2 steps whenever two-level approval is enabled AND there's real
    // evidence L2 is/was in play for this timesheet — including COMPANY scope,
    // where that evidence lives on the Timesheet row, not the entries.
    const showL2Steps = twoLevelEnabled && (ts.level2Required || l2Dispatched || l2ReviewPending || hasL2Entries);

    // L1 (manager) acts AFTER L2, so 'Pending Level 2' is NOT an L1-done state.
    // A 'Rejected' status only counts as L1-done if the manager themself rejected
    // it — if the external approver rejected first, the manager never got to act.
    const isL1Done = ts.status === 'Approved' || (ts.status === 'Rejected' && !rejectedByExternal);

    const isComplete = ts.status === 'Approved';

    // External review runs first, then internal (manager) review
    const steps = [
        { label: 'Submitted', done: true },
        ...(showL2Steps
            ? [
                  {
                      label: 'External Approval',
                      done: l2ReviewDone,
                      pending: l2ReviewPending,
                      failed: l2ReviewFailed,
                  },
              ]
            : []),
        {
            label: 'Internal Review',
            done: isL1Done,
            failed: ts.status === 'Rejected' && !rejectedByExternal,
        },
        { label: 'Complete', done: isComplete },
    ];

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', px: { xs: 0.5, md: 0 } }}>
            {steps.map((step, i) => (
                <React.Fragment key={step.label}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.75,
                            flexShrink: 0,
                            width: { xs: 40, md: 52 },
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 22, md: 28 },
                                height: { xs: 22, md: 28 },
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: step.failed
                                    ? '#fef2f2'
                                    : step.done
                                    ? '#ecfdf5'
                                    : step.pending
                                    ? '#fff7ed'
                                    : '#f1f5f9',
                                border: `2px solid ${
                                    step.failed
                                        ? '#fca5a5'
                                        : step.done
                                        ? '#6ee7b7'
                                        : step.pending
                                        ? '#fdba74'
                                        : '#e2e8f0'
                                }`,
                            }}
                        >
                            {step.failed ? (
                                <CancelledIcon sx={{ fontSize: { xs: 11, md: 13 }, color: '#dc2626' }} />
                            ) : step.done ? (
                                <CheckedIcon sx={{ fontSize: { xs: 11, md: 13 }, color: '#059669' }} />
                            ) : step.pending ? (
                                <PendingIcon sx={{ fontSize: { xs: 11, md: 13 }, color: '#f59e0b' }} />
                            ) : (
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: '#cbd5e1',
                                    }}
                                />
                            )}
                        </Box>

                        <Typography
                            sx={{
                                fontSize: { xs: 8, md: 9.5 },
                                fontWeight: 600,
                                textAlign: 'center',
                                lineHeight: 1.3,
                                color: step.failed
                                    ? '#dc2626'
                                    : step.done
                                    ? '#059669'
                                    : step.pending
                                    ? '#f59e0b'
                                    : '#94a3b8',
                            }}
                        >
                            {step.label}
                        </Typography>
                    </Box>

                    {i < steps.length - 1 && (
                        <Box
                            sx={{
                                flex: 1,
                                height: 2,
                                minWidth: { xs: 4, md: 6 },
                                mt: { xs: 1.375, md: 1.75 },
                                bgcolor: step.done ? '#6ee7b7' : '#e2e8f0',
                            }}
                        />
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
}

// ───────────────────────────────────────────────────────────────────────────────
// Main Component
// ───────────────────────────────────────────────────────────────────────────────

export default function TimesheetReview({
    open,
    onClose,
    timesheet,
    onAction,
    standalone = false,
    readOnly = false,
}) {
    const [decision, setDecision] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Inline entry edit state
    const [editingId, setEditingId] = useState(null);
    const [editVals, setEditVals] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    // Local copy of entries (updated after inline edit saves)
    const [localEntries, setLocalEntries] = useState([]);

    const [globalConfig, setGlobalConfig] = useState(null);

    useEffect(() => {
        getData(API_ENDPOINTS.getTimesheetConfig())
            .then(res => setGlobalConfig(res?.result?.data ?? res?.result ?? null))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (open) {
            setDecision('');
            setComment('');
            setSubmitting(false);
            setSubmitError('');
            setEditingId(null);
            setEditVals({});
            setEditSaving(false);
            setEditError('');
        }
    }, [open, timesheet?.id]);

    useEffect(() => {
        if (open && timesheet?.entries) {         
            const sorted = [...timesheet.entries].sort((a,b) => new Date(a.date) - new Date(b.date))
               setLocalEntries(sorted);
        }
    }, [open, timesheet?.id, timesheet?.entries]);

    if (!timesheet) return null;

    const totalHours = timesheet.totalHours || 0;
    const overtimeHours = timesheet.overtimeHours || 0;
    const billable = timesheet.billableHours || 0;

    const maxWeeklyHours = globalConfig?.max_weekly_hours != null ? parseFloat(globalConfig.max_weekly_hours) : null;
    const isOverWeeklyMax = maxWeeklyHours != null && totalHours > maxWeeklyHours;

    const billableRate =
        totalHours > 0
            ? Math.round((billable / totalHours) * 100)
            : 0;

    const statusCfg =
        STATUS_CFG[timesheet.status] || STATUS_CFG.Draft;

    // Hide Approve/Reject once timesheet has reached a terminal/post-approval state
    const isAlreadyActioned = [
        'Approved',
        'Rejected',
    ].includes(timesheet.status);

    const canAct = !isAlreadyActioned && !readOnly;

    // External-first flow: only relevant when two-level approval is enabled globally
    const twoLevelEnabled = !!globalConfig?.is_two_level_approval;
    const hasPendingExternalEntries = twoLevelEnabled && localEntries.some(e => ['PENDING', 'REJECTED'].includes(e.l2_status?.toUpperCase()));
    // level2_approver_id in the `timesheet` prop is not reliably COMPANY-scope-only —
    // getTimesheetById falls back to a project's external_approver_id for display when
    // the raw DB column is null, which happens for every genuine PROJECT-scope timesheet.
    // Only treat this as a COMPANY-scope pending gate when there are no per-project L2
    // entries at all — PROJECT scope already has its own correct gate above.
    const pendingCompanyExternal = twoLevelEnabled
        && !localEntries.some(e => e.needs_l2_approval)
        && !!(timesheet.level2_approver_id && !timesheet.level2_approved_at);
    const approveBlocked = hasPendingExternalEntries || pendingCompanyExternal || localEntries.some(e => ['PENDING', 'REJECTED'].includes(e.l2_status?.toUpperCase()));

    // Manager inline edit handlers
    const startEdit = (e) => {
        setEditingId(e.id);
        setEditVals({
            hours_worked: e.hours,
            work_description: e.description || '',
            remarks: e.remarks || '',
            location: e.location || '',
            is_billable: e.billable === 'Billable',
        });
        setEditError('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditVals({});
        setEditError('');
    };

    const saveEdit = async () => {
        const hrs = parseFloat(editVals.hours_worked);
        if (!hrs || hrs <= 0) {
            setEditError('Hours must be greater than 0.');
            return;
        }
        setEditSaving(true);
        setEditError('');
        try {
            await putData(API_ENDPOINTS.editTeamTimesheet(timesheet.id), {
                entries: [{
                    id: editingId,
                    hours_worked: hrs,
                    work_description: editVals.work_description,
                    remarks: editVals.remarks,
                    location: editVals.location,
                    is_billable: editVals.is_billable,
                }],
            });
            setLocalEntries(prev => prev.map(e => {
                if (e.id !== editingId) return e;
                return {
                    ...e,
                    hours: hrs,
                    description: editVals.work_description,
                    remarks: editVals.remarks,
                    location: editVals.location,
                    billable: editVals.is_billable ? 'Billable' : 'Non-Billable',
                };
            }));
            setEditingId(null);
        } catch (err) {
            setEditError(err?.response?.data?.message || err?.message || 'Edit failed. Please try again.');
        } finally {
            setEditSaving(false);
        }
    };

    const renderEditForm = () => (
        <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                <TextField
                    label="Hours"
                    type="number"
                    size="small"
                    value={editVals.hours_worked}
                    onChange={ev => setEditVals(p => ({ ...p, hours_worked: ev.target.value }))}
                    inputProps={{ min: 0.5, max: 24, step: 0.5 }}
                    sx={{ width: 100 }}
                />
                <Select
                    size="small"
                    value={editVals.is_billable ? 'billable' : 'non-billable'}
                    onChange={ev => setEditVals(p => ({ ...p, is_billable: ev.target.value === 'billable' }))}
                    sx={{
                        fontSize: 12, borderRadius: 1.5, height: 40,
                        bgcolor: editVals.is_billable ? '#ecfdf5' : '#f5f3ff',
                        color: editVals.is_billable ? '#059669' : '#7c3aed',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: editVals.is_billable ? '#6ee7b7' : '#ddd6fe' },
                        '& .MuiSvgIcon-root': { color: editVals.is_billable ? '#059669' : '#7c3aed' },
                        '& .MuiSelect-select': { py: 0.75, px: 1.5 },
                        minWidth: 130,
                    }}
                >
                    <MenuItem value="billable" sx={{ fontSize: 12 }}>Billable</MenuItem>
                    <MenuItem value="non-billable" sx={{ fontSize: 12 }}>Non-Billable</MenuItem>
                </Select>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25 }}>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={saveEdit}
                        disabled={editSaving}
                        startIcon={editSaving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', boxShadow: 'none', fontSize: 12 }}
                    >
                        {editSaving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                        size="small"
                        onClick={cancelEdit}
                        disabled={editSaving}
                        sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', fontSize: 12 }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
            {editError && (
                <Typography sx={{ mt: 1, fontSize: 11, color: '#dc2626' }}>{editError}</Typography>
            )}
        </>
    );

    const handleSubmit = () => {
        if (!decision) {
            setSubmitError('Please select Approve or Reject.');
            return;
        }

        if (decision === 'Reject' && !comment.trim()) {
            setSubmitError('Rejection reason is required.');
            return;
        }

        setSubmitting(true);

        setTimeout(() => {
            onAction(timesheet.id, decision, comment.trim());

            setSubmitting(false);
            if (!standalone) onClose();
        }, 700);
    };

    const _pageContent = (
        <>
            {/* HEADER */}

            <Box
                sx={{
                    bgcolor: '#fff',
                    borderBottom: '1px solid #e2e8f0',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexShrink: 0,
                }}
            >
                <Avatar
                    sx={{
                        width: {xs: 38, sm: 42},
                        height: {xs: 38, sm: 42},
                        bgcolor: '#6366f1',
                        fontWeight: {xs: 700,sm: 800},
                        borderRadius: 2,
                    }}
                >
                    {timesheet.employeeAvatar}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: {xs: 13, sm: 16},
                                color: '#0f172a',
                            }}
                        >
                            {timesheet.employeeName}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: {xs: 10, sm: 12},
                                color: '#64748b',
                            }}
                        >
                            {timesheet.employeeRole} · {timesheet.employeeDept}
                        </Typography>

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: {xs: 1.05, sm: 1.25},
                                py: {xs: 0.2, sm: 0.35},
                                borderRadius: 10,
                                bgcolor: statusCfg.bg,
                                border: `1px solid ${statusCfg.border}`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: statusCfg.dot,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: {xs: 9, sm: 11},
                                    fontWeight: 700,
                                    color: statusCfg.color,
                                }}
                            >
                                {timesheet.status === 'Submitted' && timesheet.resubmission_count
                                    ? 'Resubmitted'
                                    : (timesheet.status === 'Pending Level 2' ? 'Pending External Approval' : timesheet.status)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            mt: 0.25,
                        }}
                    >
                        <CalendarIcon
                            sx={{
                                fontSize: 12,
                                color: '#94a3b8',
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: {xs: 10, sm: 12},
                                color: '#64748b',
                            }}
                        >
                            {fmtDate(timesheet.weekStart)} –{' '}
                            {fmtDate(timesheet.weekEnd)}
                        </Typography>
                    </Box>
                </Box>

                {/* ONLY 3 KPIs */}

                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        gap: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <StatPill
                        icon={ScheduleIcon}
                        label="Total Hrs"
                        value={`${totalHours}h`}
                        accent="#6366f1"
                        subtitle={maxWeeklyHours != null ? `of ${maxWeeklyHours}h limit` : undefined}
                        subtitleColor={isOverWeeklyMax ? '#dc2626' : undefined}
                    />

                    <StatPill
                        icon={WorkIcon}
                        label="Overtime"
                        value={
                            overtimeHours > 0
                                ? `+${overtimeHours}h`
                                : '—'
                        }
                        accent="#f59e0b"
                    />

                    <StatPill
                        icon={TrendingUpIcon}
                        label="Billable"
                        value={`${billableRate}%`}
                        accent="#10b981"
                    />
                </Box>

                <IconButton
                    onClick={onClose}
                    sx={{ color: '#64748b' }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* BODY */}

            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    overflow: { xs: 'auto', md: 'hidden' },
                    minHeight: 0,
                    minWidth: 0,
                }}
            >
                {/* LEFT */}

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflowY: { xs: 'visible', md: 'auto' },
                        px: { xs: 2},
                        py: 2.5,
                    }}
                >
                    {/* TABLE */}

                    <Box
                        sx={{
                            bgcolor: '#fff',
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden',
                            mb: 2.5,
                        }}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: 13,
                                    color: '#0f172a',
                                }}
                            >
                                Timesheet Entries
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: '#94a3b8',
                                }}
                            >
                                {localEntries.length || 0} entries
                            </Typography>
                        </Box>

                        {/* MOBILE CARDS — replaces the table below md */}

                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            {(() => {
                                const showEditColMobile = !readOnly && timesheet.status === 'Submitted';
                                const showL2ColMobile = twoLevelEnabled && localEntries.some(e => e.l2_status && e.l2_status !== 'NOT_REQUIRED');
                                const L2_CFG = {
                                    PENDING: { label: 'Pending', bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
                                    APPROVED: { label: 'Approved', bg: '#ecfdf5', color: '#059669', border: '#6ee7b7' },
                                    REJECTED: { label: 'Rejected', bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
                                    NOT_REQUIRED: { label: 'N/A', bg: '#f1f5f9', color: '#94a3b8', border: '#e2e8f0' },
                                };
                                const DAY_TYPE_CFG_MOBILE = {
                                    working:  { color: '#059669', bg: '#ecfdf5', border: '#6ee7b7', dot: '#059669' },
                                    leave:    { color: '#c2410c', bg: '#fff7ed', border: '#fdba74', dot: '#f59e0b' },
                                    holiday:  { color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6' },
                                    activity: { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', dot: '#8b5cf6' },
                                    weekend:  { color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', dot: '#94a3b8' },
                                };

                                return localEntries.map((e, i) => {
                                    const hideBillable = Number(e.hours) === 0;
                                    const dayCfg = e.day ? (DAY_TYPE_CFG_MOBILE[e.day.toLowerCase()] || { color: '#475569', bg: '#f1f5f9', border: '#e2e8f0', dot: '#94a3b8' }) : null;
                                    const l2Cfg = L2_CFG[e.l2_status] || null;

                                    return (
                                        <React.Fragment key={e.id ?? i}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderBottom: i < localEntries.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                    ...(e.l2_status === 'APPROVED' ? { bgcolor: '#f0fdf4' } : e.l2_status === 'REJECTED' ? { bgcolor: '#fef2f2' } : {}),
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 0.4 }}>
                                                            {fmtDate(e.date)}
                                                        </Typography>
                                                        {dayCfg && (
                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 10, bgcolor: dayCfg.bg, border: `1px solid ${dayCfg.border}` }}>
                                                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: dayCfg.dot, flexShrink: 0 }} />
                                                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: dayCfg.color, lineHeight: 1 }}>{e.day}</Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                        {Number(e.hours) === 0 ? (
                                                            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>—</Typography>
                                                        ) : (
                                                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{e.hours}h</Typography>
                                                        )}
                                                        {e.overtime > 0 && (
                                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>+{e.overtime} OT</Typography>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Box sx={{ mt: 1 }}>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{e.project || '—'}</Typography>
                                                    {e.task && (
                                                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>{e.task}</Typography>
                                                    )}
                                                </Box>

                                                {e.description && (
                                                    <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.5, mt: 0.75 }}>{e.description}</Typography>
                                                )}

                                                {e.remarks && (
                                                    <Box sx={{ mt: 1, p: 1, bgcolor: '#fff7ed', borderRadius: 1.5, borderLeft: '3px solid #fb923c' }}>
                                                        <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.4 }}>Remark</Typography>
                                                        <Typography sx={{ fontSize: 11, color: '#92400e', fontStyle: 'italic', lineHeight: 1.55, wordBreak: 'break-word' }}>{e.remarks}</Typography>
                                                    </Box>
                                                )}

                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1.25 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        {!hideBillable && (
                                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: 10, bgcolor: e.billable === 'Billable' ? '#ecfdf5' : '#f5f3ff', border: `1px solid ${e.billable === 'Billable' ? '#6ee7b7' : '#ddd6fe'}` }}>
                                                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: e.billable === 'Billable' ? '#059669' : '#7c3aed' }} />
                                                                <Typography sx={{ fontSize: 11, fontWeight: 600, color: e.billable === 'Billable' ? '#059669' : '#7c3aed' }}>{e.billable}</Typography>
                                                            </Box>
                                                        )}
                                                        <RowAttachments attachments={e.attachments} />
                                                        {showL2ColMobile && l2Cfg && (
                                                            <Chip label={l2Cfg.label} size="small" sx={{ bgcolor: l2Cfg.bg, color: l2Cfg.color, border: `1px solid ${l2Cfg.border}`, fontSize: 10, fontWeight: 700, height: 20 }} />
                                                        )}
                                                    </Box>

                                                    {showEditColMobile && e.manager_can_edit_timesheet && editingId !== e.id && (
                                                        <Tooltip title="Edit entry">
                                                            <IconButton size="small" onClick={() => startEdit(e)} sx={{ color: '#6366f1' }}>
                                                                <EditIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>

                                            {editingId === e.id && (
                                                <Box sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', p: 1.5 }}>
                                                    {renderEditForm()}
                                                </Box>
                                            )}
                                        </React.Fragment>
                                    );
                                });
                            })()}
                        </Box>

                        <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
                            <Table sx={{ minWidth: 700, tableLayout: 'fixed' }}>
                                {(() => {
                                    const showEditCol = !readOnly && timesheet.status === 'Submitted';
                                    // Show L2 status column only when two-level approval is globally enabled
                                    const showL2Col = twoLevelEnabled && localEntries.some(e => e.l2_status && e.l2_status !== 'NOT_REQUIRED');
                                    const headers = ['Date', 'Project / Task', 'Description', 'Hours', 'Type', 'Files'];

                                    if (showL2Col) headers.push('Ext. Status');
                                    if (showEditCol) headers.push('');

                                    // table-layout: fixed sizes columns from this header row alone, so the
                                    // remaining space always goes to Description ('auto') instead of being
                                    // spread unpredictably across narrow columns like Files/Ext. Status.
                                    const COLUMN_WIDTHS = {
                                        'Date': '12%',
                                        'Project / Task': '18%',
                                        'Description': 'auto',
                                        'Hours': '7%',
                                        'Type': '9%',
                                        'Files': '7%',
                                        'Ext. Status': '9%',
                                        '': 40,
                                    };

                                    return (
                                        <>
                                        <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {headers.map((h) => (
                                            <TableCell
                                                key={h}
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: 11,
                                                    color: '#64748b',
                                                    py: 1.25,
                                                    textTransform: 'uppercase',
                                                    borderBottom:
                                                        '1px solid #e2e8f0',
                                                    width: COLUMN_WIDTHS[h],
                                                    ...(h === '' ? { p: 0 } : { px: 1 }),
                                                }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {localEntries.map((e, i) => (
                                        <React.Fragment key={e.id ?? i}>
                                        <TableRow
                                            sx={{
                                                ...(e.l2_status === 'APPROVED' ? { bgcolor: '#f0fdf4' } : e.l2_status === 'REJECTED' ? { bgcolor: '#fef2f2' } : {}),
                                                '&:hover': {
                                                    bgcolor: e.l2_status === 'APPROVED' ? '#dcfce7' : e.l2_status === 'REJECTED' ? '#fee2e2' : '#fafbff',
                                                },
                                            }}
                                        >
                                            {/* DATE */}

                                            <TableCell
                                                sx={{
                                                    py: 1.75,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    // whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: '#0f172a',
                                                        mb: 0.6,
                                                    }}
                                                >
                                                    {fmtDate(e.date)}
                                                </Typography>

                                                {e.day && (() => {
                                                    const DAY_TYPE_CFG = {
                                                        working:  { color: '#059669', bg: '#ecfdf5', border: '#6ee7b7', dot: '#059669' },
                                                        leave:    { color: '#c2410c', bg: '#fff7ed', border: '#fdba74', dot: '#f59e0b' },
                                                        holiday:  { color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe', dot: '#3b82f6' },
                                                        activity: { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', dot: '#8b5cf6' },
                                                        weekend:  { color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', dot: '#94a3b8' },
                                                    };
                                                    const key = e.day.toLowerCase();
                                                    const cfg = DAY_TYPE_CFG[key] || { color: '#475569', bg: '#f1f5f9', border: '#e2e8f0', dot: '#94a3b8' };
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                px: 1,
                                                                py: 0.25,
                                                                borderRadius: 10,
                                                                bgcolor: cfg.bg,
                                                                border: `1px solid ${cfg.border}`,
                                                            }}
                                                        >
                                                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
                                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: cfg.color, lineHeight: 1 }}>
                                                                {e.day}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })()}
                                            </TableCell>

                                            {/* PROJECT + TASK */}

                                            <TableCell
                                                sx={{
                                                    py: 1.75,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    maxWidth: 220,
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: '#0f172a',
                                                        mb: 0.4,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {e.project || '—'}
                                                </Typography>

                                                {e.task && (
                                                    <Tooltip title={e.task} placement="top-start" arrow>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 11,
                                                                color: '#64748b',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                cursor: 'default',
                                                            }}
                                                        >
                                                            {e.task}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>

                                            {/* DESCRIPTION */}

                                            <TableCell
                                                sx={{
                                                    py: 1.75,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    maxWidth: 260,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                                                    {e.description || '—'}
                                                </Typography>
                                                {e.remarks && (
                                                    <Box sx={{ mt: 1.25, p: 1, bgcolor: '#fff7ed', borderRadius: 1.5, borderLeft: '3px solid #fb923c' }}>
                                                        <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.4 }}>Remark</Typography>
                                                        <Typography sx={{ fontSize: 11, color: '#92400e', fontStyle: 'italic', lineHeight: 1.55, wordBreak: 'break-word' }}>{e.remarks}</Typography>
                                                    </Box>
                                                )}
                                            </TableCell>

                                            {/* HOURS + OT */}

                                            <TableCell
                                                sx={{
                                                    py: 1.75,
                                                    borderBottom:
                                                        '1px solid #f1f5f9',
                                                    width: 50,
                                                }}
                                            >
                                                {Number(e.hours) === 0 ? (
                                                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                                                        —
                                                    </Typography>
                                                ) : (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 16,
                                                            fontWeight: 800,
                                                            color: '#0f172a',
                                                        }}
                                                    >
                                                        {e.hours}h
                                                    </Typography>
                                                )}

                                                {e.overtime > 0 && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            color: '#f59e0b',
                                                        }}
                                                    >
                                                        +{e.overtime} OT
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            {/* BILLABLE */}

                                            <TableCell
                                                sx={{
                                                    py: 1.75,
                                                    borderBottom:
                                                        '1px solid #f1f5f9',
                                                }}
                                            >
                                                {(() => {
                                                    // No hours logged (placeholder Weekend/Holiday/Leave row) means
                                                    // there's nothing to bill — but a Leave day with real hours
                                                    // against a project (e.g. WFH, or a partial-day leave that
                                                    // still has logged work) is billable just like any other entry.
                                                    const hideBillable = Number(e.hours) === 0;

                                                    if (hideBillable) {
                                                        return (
                                                            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                                                                —
                                                            </Typography>
                                                        );
                                                    }

                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                px: 1,
                                                                py: 0.3,
                                                                borderRadius: 10,
                                                                bgcolor:
                                                                    e.billable ===
                                                                    'Billable'
                                                                        ? '#ecfdf5'
                                                                        : '#f5f3ff',
                                                                border: `1px solid ${
                                                                    e.billable ===
                                                                    'Billable'
                                                                        ? '#6ee7b7'
                                                                        : '#ddd6fe'
                                                                }`,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 5,
                                                                    height: 5,
                                                                    borderRadius:
                                                                        '50%',
                                                                    bgcolor:
                                                                        e.billable ===
                                                                        'Billable'
                                                                            ? '#059669'
                                                                            : '#7c3aed',
                                                                }}
                                                            />

                                                            <Typography
                                                                sx={{
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    color:
                                                                        e.billable ===
                                                                        'Billable'
                                                                            ? '#059669'
                                                                            : '#7c3aed',
                                                                }}
                                                            >
                                                                {e.billable}
                                                            </Typography>
                                                        </Box>
                                                    );
                                                })()}
                                            </TableCell>

                                            {/* FILES */}

                                            <TableCell
                                                sx={{
                                                    py: 1.75,
                                                    borderBottom:
                                                        '1px solid #f1f5f9',
                                                    px: 1,
                                                }}
                                            >
                                                <RowAttachments
                                                    attachments={
                                                        e.attachments
                                                    }
                                                />
                                            </TableCell>

                                            {/* L2 STATUS — shown when PENDING_LEVEL_2 */}
                                            {showL2Col && (
                                                <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9', px: 1 }}>
                                                    {e.l2_status === 'PENDING' && (
                                                        <Chip label="Pending" size="small" sx={{ bgcolor: '#fff7ed', color: '#c2410c', borderColor: '#fdba74', border: '1px solid', fontSize: 10, fontWeight: 700 }} />
                                                    )}
                                                    {e.l2_status === 'APPROVED' && (
                                                        <Chip label="Approved" size="small" sx={{ bgcolor: '#ecfdf5', color: '#059669', borderColor: '#6ee7b7', border: '1px solid', fontSize: 10, fontWeight: 700 }} />
                                                    )}
                                                    {e.l2_status === 'REJECTED' && (
                                                        <Chip label="Rejected" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5', border: '1px solid', fontSize: 10, fontWeight: 700 }} />
                                                    )}
                                                    {e.l2_status === 'NOT_REQUIRED' && (
                                                        <Chip label="N/A" size="small" sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', borderColor: '#e2e8f0', border: '1px solid', fontSize: 10 }} />
                                                    )}
                                                    {e.l2_approver_name && e.l2_status === 'PENDING' && (
                                                        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
                                                            {e.l2_approver_name}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            )}

                                            {/* EDIT ICON — shown when SUBMITTED or L1_APPROVED */}
                                            {showEditCol && (
                                                <TableCell sx={{ py: 1.75, borderBottom: '1px solid #f1f5f9', px: 0.5, textAlign: 'center' }}>
                                                    {e.manager_can_edit_timesheet && editingId !== e.id && (
                                                        <Tooltip title="Edit entry">
                                                            <IconButton size="small" onClick={() => startEdit(e)} sx={{ color: '#6366f1' }}>
                                                                <EditIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>

                                        {/* INLINE EDIT ROW */}
                                        {editingId === e.id && (
                                            <TableRow key={`edit-${e.id}`}>
                                                <TableCell colSpan={showEditCol ? (showL2Col ? 8 : 7) : 6} sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', p: 2 }}>
                                                    {renderEditForm()}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                    ))}
                                </TableBody>
                                </>
                            );
                        })()}
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* EMPLOYEE NOTE */}

                    {timesheet.employeeComment && (
                        <Box
                            sx={{
                                bgcolor: '#fff',
                                borderRadius: 2,
                                border: '1px solid #e2e8f0',
                                p: 2.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    mb: 1,
                                }}
                            >
                                Employee Note
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#334155',
                                    lineHeight: 1.6,
                                }}
                            >
                                {timesheet.employeeComment}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* SIDEBAR */}

                <Box
                    sx={{
                        width: { xs: '100%', md: 'calc(225px + 10%)' },
                        minWidth: 0,
                        flexShrink: 0,
                        borderLeft: { xs: 'none', md: '1px solid #e2e8f0' },
                        borderTop: { xs: '1px solid #e2e8f0', md: 'none' },
                        bgcolor: '#fff',
                        overflowY: { xs: 'visible', md: 'auto' },
                        p: { xs: 3, md: 2.5 },
                    }}
                >
                    {/* FLOW */}

                    <Box sx={{ mb: 2.5 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#94a3b8',
                                textTransform: 'uppercase',
                                mb: 1.5,
                            }}
                        >
                            Approval Flow
                        </Typography>

                        <Box sx={{ overflowX: 'auto' }}>
                            <ApprovalTimeline ts={timesheet} twoLevelEnabled={twoLevelEnabled} localEntries={localEntries} />
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 2.5 }} />

                    {/* COMPANY scope: external pre-approval pending — only when two-level is enabled */}
                    {twoLevelEnabled && pendingCompanyExternal && canAct && (
                        <Alert
                            severity="warning"
                            icon={<WarningIcon fontSize="small" />}
                            sx={{ mb: 2, borderRadius: 2 }}
                        >
                            This timesheet is pending external review. The external approver must complete their review before you can approve. You can still reject it if needed.
                        </Alert>
                    )}

                    {/* External pre-approval done — ready to approve */}
                    {twoLevelEnabled && localEntries.some(e => e.l2_status === 'APPROVED') && !localEntries.some(e => ['PENDING', 'REJECTED'].includes(e.l2_status?.toUpperCase())) && canAct && (
                        <Alert
                            severity="success"
                            sx={{ mb: 2, borderRadius: 2 }}
                        >
                            External review complete. You can now give final approval.
                        </Alert>
                    )}

                    {/* L2 REVIEW — unified compact block, only when two-level approval is globally enabled */}
                    {twoLevelEnabled && !['Approved', 'Rejected'].includes(timesheet.status) && (() => {
                        const allL2Entries = localEntries.filter(e => e.needs_l2_approval);
                        if (!allL2Entries.length) return null;

                        const approved = localEntries.filter(e => e.needs_l2_approval && e.l2_status === 'APPROVED');
                        const rejected = localEntries.filter(e => e.needs_l2_approval && e.l2_status === 'REJECTED');
                        // Anything needing L2 that isn't APPROVED or REJECTED yet is still
                        // "awaiting" — this covers real PENDING entries AND entries that were
                        // never dispatched at all (l2_status: null), which is common for
                        // COMPANY-scope timesheets where per-entry l2_status is never touched.
                        const awaiting = localEntries.filter(e => e.needs_l2_approval && e.l2_status !== 'APPROVED' && e.l2_status !== 'REJECTED');
                        const exempt   = localEntries.filter(e => e.l2_status === 'NOT_REQUIRED');
                        // "no PENDING left" is not the same as "actually approved" — an entry
                        // that was never dispatched to L2 at all has l2_status: null, which is
                        // neither PENDING nor APPROVED. Require every L2 entry to genuinely be
                        // APPROVED before calling it done.
                        const allDone  = allL2Entries.every(e => e.l2_status === 'APPROVED');

                        // PROJECT scope only: which projects on this timesheet don't have
                        // manager auto-approval enabled — these are the reason the manager
                        // still has to act even after every external review is done.
                        const isProjectScope = globalConfig?.approval_scope === 'PROJECT';
                        const blockingProjects = isProjectScope
                            ? [...new Set(
                                allL2Entries
                                    .filter(e => !e.auto_approve_eligible)
                                    .map(e => e.project_name || 'Unknown project')
                            )]
                            : [];

                        const entryCfg = {
                            PENDING:  { label: 'Awaiting', bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
                            APPROVED: { label: 'Approved', bg: '#ecfdf5', color: '#059669', border: '#6ee7b7' },
                            REJECTED: { label: 'Rejected', bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
                        };

                        const groupedByProject = [...awaiting, ...approved, ...rejected].reduce((acc, e) => {
                            const projectName = e.project_name || e.project || 'Unknown Project';
                            const approverName = e.l2_approver_name || 'Unknown Approver';
                            const status = e.l2_status || 'PENDING';

                            if (!acc[projectName]) acc[projectName] = { isProjectScope, entries: [] };
                            
                            const existing = acc[projectName].entries.find(item => item.approverName === approverName && item.status === status);
                            if (existing) {
                                existing.count += 1;
                            } else {
                                acc[projectName].entries.push({
                                    approverName,
                                    status,
                                    count: 1,
                                    auto_approve_eligible: e.auto_approve_eligible
                                });
                            }
                            return acc;
                        }, {});

                        return (
                            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: allDone ? '#ecfdf5' : '#fff7ed', border: `1px solid ${allDone ? '#6ee7b7' : '#fdba74'}`, mb: 2 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: allDone ? '#065f46' : '#92400e', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
                                    External Approval · {allDone ? 'All approved' : 'Pending'}
                                </Typography>

                                {Object.entries(groupedByProject).map(([projectName, data], i) => (
                                    <Box key={i} sx={{ mb: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#334155' }} noWrap>
                                                {projectName}
                                            </Typography>
                                            {isProjectScope && data.entries.some(e => e.auto_approve_eligible) && (
                                                <Chip
                                                    label="⚡ Auto"
                                                    size="small"
                                                    sx={{
                                                        fontSize: 8, fontWeight: 700, height: 15, px: 0.25,
                                                        bgcolor: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe',
                                                    }}
                                                />
                                            )}
                                            {isProjectScope && !data.entries.some(e => e.auto_approve_eligible) && (
                                                <Chip
                                                    label="Manual"
                                                    size="small"
                                                    sx={{
                                                        fontSize: 8, fontWeight: 700, height: 15, px: 0.25,
                                                        bgcolor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        
                                        {data.entries.map((entry, j) => {
                                            const cfg = entryCfg[entry.status] || entryCfg.PENDING;
                                            return (
                                                <Box key={j} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                    <Box sx={{ flex: 1, mr: 1, overflow: 'hidden' }}>
                                                        <Typography sx={{ fontSize: 10, color: '#64748b' }} noWrap>
                                                            {entry.approverName} • {entry.count} {entry.count === 1 ? 'entry' : 'entries'}
                                                        </Typography>
                                                    </Box>
                                                    <Chip label={cfg.label} size="small" sx={{ fontSize: 9, fontWeight: 700, height: 18, bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }} />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))}

                                {!allDone && (
                                    <Typography sx={{ fontSize: 10, color: '#92400e', mt: 0.75 }}>
                                        Approval locked until external review completes
                                    </Typography>
                                )}

                                {allDone && blockingProjects.length > 0 && (
                                    <Typography sx={{ fontSize: 10, color: '#4338ca', mt: 0.75 }}>
                                        Manual approval required — {blockingProjects.join(', ')} {blockingProjects.length === 1 ? "doesn't" : "don't"} have auto-approval enabled.
                                    </Typography>
                                )}
                            </Box>
                        );
                    })()}

                    {/* ACTION */}

                    {canAct && (
                        <>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                    mb: 1.25,
                                }}
                            >
                                Your Decision
                            </Typography>

                            <ToggleButtonGroup
                                value={decision}
                                exclusive
                                fullWidth
                                onChange={(_, v) => {
                                    if (v) {
                                        setDecision(v);
                                        setSubmitError('');
                                    }
                                }}
                                sx={{
                                    gap: 1,
                                    mb: 2,
                                    '& .MuiToggleButtonGroup-grouped': {
                                        border: 'none !important',
                                        borderRadius: '10px !important',
                                    },
                                }}
                            >
                                <Tooltip
                                    title={approveBlocked
                                        ? (hasPendingExternalEntries
                                            ? `Waiting for external review on ${localEntries.filter(e => ['PENDING', 'REJECTED'].includes(e.l2_status?.toUpperCase())).length} entr${localEntries.filter(e => ['PENDING', 'REJECTED'].includes(e.l2_status?.toUpperCase())).length === 1 ? 'y' : 'ies'}`
                                            : 'Waiting for external review to complete')
                                        : ''}
                                    arrow
                                >
                                    <span style={{ flex: 1 }}>
                                <ToggleButton
                                    value="Approve"
                                    disabled={approveBlocked}
                                    sx={{
                                        width: '100%',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        py: 1.25,
                                        border:
                                            '1.5px solid #d1fae5 !important',
                                        '&.Mui-selected': {
                                            bgcolor: '#ecfdf5',
                                            color: '#059669',
                                        },
                                        '&.Mui-disabled': {
                                            opacity: 0.45,
                                            cursor: 'not-allowed',
                                        },
                                    }}
                                >
                                    Approve
                                </ToggleButton>
                                    </span>
                                </Tooltip>

                                <ToggleButton
                                    value="Reject"
                                    sx={{
                                        flex: 1,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        py: 1.25,
                                        border:
                                            '1.5px solid #fee2e2 !important',
                                        '&.Mui-selected': {
                                            bgcolor: '#fef2f2',
                                            color: '#dc2626',
                                        },
                                    }}
                                >
                                    Reject
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder={
                                    decision === 'Reject'
                                        ? 'Reason is required...'
                                        : 'Add a comment...'
                                }
                                value={comment}
                                onChange={(e) =>
                                    setComment(e.target.value)
                                }
                                sx={{
                                    mb: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        fontSize: 13,
                                    },
                                }}
                            />

                            {submitError && (
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: '#dc2626',
                                        mb: 1.5,
                                    }}
                                >
                                    {submitError}
                                </Typography>
                            )}

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={
                                    submitting ||
                                    !decision ||
                                    (decision === 'Reject' &&
                                        !comment.trim())
                                }
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    py: 1.25,
                                    fontWeight: 700,
                                    bgcolor:
                                        decision === 'Approve'
                                            ? '#059669'
                                            : '#dc2626',
                                    boxShadow: 'none',
                                }}
                            >
                                {submitting
                                    ? 'Submitting...'
                                    : decision === 'Approve'
                                    ? 'Approve Timesheet'
                                    : 'Reject Timesheet'}
                            </Button>
                        </>
                    )}


                    <Divider sx={{ my: 2.5 }} />

                    {/* HISTORY */}

                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1.5,
                            }}
                        >
                            <HistoryIcon
                                sx={{
                                    fontSize: 14,
                                    color: '#94a3b8',
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#94a3b8',
                                    textTransform: 'uppercase',
                                }}
                            >
                                History
                            </Typography>
                        </Box>

                        {timesheet.approvalHistory?.length > 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}
                            >
                                {timesheet.approvalHistory.map((h, i) => (
                                    <Box key={i}>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: '#0f172a',
                                            }}
                                        >
                                            {h.actor}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color:
                                                    h.action === 'Approved'
                                                        ? '#059669'
                                                        : '#dc2626',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {h.action}
                                        </Typography>

                                        {h.comment && (
                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: '#64748b',
                                                    mt: 0.25,
                                                }}
                                            >
                                                "{h.comment}"
                                            </Typography>
                                        )}

                                        <Typography
                                            sx={{
                                                fontSize: 10,
                                                color: '#94a3b8',
                                                mt: 0.25,
                                            }}
                                        >
                                            {h.timestamp}
                                        </Typography>

                                        {i <
                                            timesheet.approvalHistory.length -
                                                1 && (
                                            <Divider
                                                sx={{ mt: 1.5 }}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: '#cbd5e1',
                                }}
                            >
                                No history yet
                            </Typography>
                        )}
                    </Box>

                    <Divider sx={{ my: 2.5 }} />

                    {/* Phase 2 — Version History accordion
                    <Accordion
                        elevation={0}
                        disableGutters
                        sx={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px !important',
                            mb: 2.5,
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                                    Version History
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                            <TimesheetVersionHistory
                                versionHistory={timesheet.versionHistory}
                                canRestore={false}
                            />
                        </AccordionDetails>
                    </Accordion>
                    */}

                    {/* MINIMAL DETAILS */}

                    <Box>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#94a3b8',
                                textTransform: 'uppercase',
                                mb: 1.25,
                            }}
                        >
                            Details
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: '#94a3b8',
                                }}
                            >
                                Submitted
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#0f172a',
                                }}
                            >
                                {fmtDate(timesheet.submittedOn)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );

    if (standalone) {
        return (
            <Box
                sx={{
                    bgcolor: '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 70px)',
                    overflow: 'hidden',
                }}
            >
                {_pageContent}
            </Box>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', md: '92vw', lg: '88vw' },
                    maxWidth: 1380,
                    bgcolor: '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            {_pageContent}
        </Drawer>
    );
}